import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import Cal from '@calcom/embed-react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {Box, Theme} from '@mui/material'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {useCalEvent} from '@progyny/hooks/useCalEvent'
import {showConsultationCancelledToast} from '@progyny/pages/Consultations/components/ConsultationToast'
import {
  REACT_APP_CALCOM_EMBED_URL,
  REACT_APP_CALCOM_ORIGIN,
} from '@progyny/utils/env'

interface CancelConsultationProps {
  cancelUrl: string
  onBack: () => void
}

const getBookingUid = (cancelUrl: string): string => {
  try {
    const {pathname} = new URL(cancelUrl)
    return pathname.split('/').filter(Boolean).pop() ?? ''
  } catch (error) {
    return cancelUrl.split('?')[0].split('/').filter(Boolean).pop() ?? ''
  }
}

const CancelConsultation: React.FC<CancelConsultationProps> = ({
  cancelUrl,
  onBack,
}) => {
  const {t} = useTranslation()
  const {classes} = useStyles()

  const bookingUid = useMemo(() => getBookingUid(cancelUrl), [cancelUrl])
  const namespace = useMemo(() => `cancel-${bookingUid}`, [bookingUid])

  useCalEvent(
    namespace,
    'bookingCancelled',
    () => {
      onBack()
      showConsultationCancelledToast()
    },
    Boolean(bookingUid)
  )

  useCalEvent(namespace, 'cancellationDismissed', onBack, Boolean(bookingUid))

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <PGButton
          variant='text'
          label={t('common_back')}
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
        />
      </Box>
      <Box className={classes.iframeWrapper}>
        {bookingUid && (
          <Cal
            namespace={namespace}
            calLink={`booking/${bookingUid}`}
            config={{theme: 'light', cancel: 'true'}}
            calOrigin={REACT_APP_CALCOM_ORIGIN}
            embedJsUrl={REACT_APP_CALCOM_EMBED_URL}
            className={classes.iframe}
            title={t('consultations_cancel_modal_iframe_title')}
          />
        )}
      </Box>
    </Box>
  )
}

export default CancelConsultation

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    gap: theme.spacing(3),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
  },
  iframeWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '1100px',
  },
  iframe: {
    width: '100%',
    minHeight: '1000px',
    border: 'none',
    borderRadius: theme.spacing(1),
    boxShadow: '0px 3px 16px rgba(190, 192, 198, 0.24)',
  },
}))
